'use client';

// imports
import { useEffect, useState, } from "react";

// lib files
import {
  LIVESTREAM_DEFAULT_FEED_PROFILE,
  LIVESTREAM_POSSIBLE_CHANNELS,
} from "@/lib/constants";
import { StationData, StationLivestreamFeed } from "@/lib/types/api/stations-data";
import { Theme } from "@/lib/types/api/multi-livestream";

// components
import MultiLivestreamError from "@/components/MultiLivestream/MultiLivestreamError";
import MultiLivestreamPlayer from "@/components/MultiLivestream/MultiLivestreamPlayer";
import MultiLivestreamSchedule from "@/components/MultiLivestream/Schedule/Schedule";
import MultiLivestreamStationInfo from "@/components/MultiLivestream/MultiLivestreamStationInfo";
import PageTransition from "@/components/Transitions/PageTransition";

// styles
import styles from '@/components/MultiLivestream/MultiLivestream.module.scss';

interface MultiLivestreamProps {
  host: string;
  isIframe: boolean;
  isSVP: boolean;
  stationData: StationData;
  theme: Theme;
}

function MultiLivestream(props: MultiLivestreamProps) {
  const { host, isIframe, isSVP, stationData, theme } = props;
  const { id: stationId, attributes } = stationData;
  const {
    livestream_feeds,
    secondary_timezone,
    short_common_name,
    timezone,
    tvss_url,
  } = attributes;

  const [dataFetchError, setDataFetchError] = useState(false);

  // @TODO: update to human-readable profile slug, if/when available from CS
  const [selectedChannel, setSelectedChannel] = useState('');
  const [userSelectedChannel, setUserSelectedChannel] = useState('');

  // Figuring out which channel is selected on mount
  useEffect(() => {
    // use the default at first
    let profile = LIVESTREAM_DEFAULT_FEED_PROFILE;

    // check for the selected query param
    // This allows us to link directly to a particular channel, e.g.
    // pbs.org/livestream/?selected=ga-world
    const queryParams = new URLSearchParams(location.search);
    const selectedParam = queryParams.get('selected');
    // make sure it's in our list of possible channels
    if (selectedParam && LIVESTREAM_POSSIBLE_CHANNELS.includes(selectedParam)) {
      profile = selectedParam;
    }

    // get the right feed
    const selectedFeed = livestream_feeds.find(
      (livestream: StationLivestreamFeed) => livestream.profile === profile
    );
    if (!selectedFeed || !selectedFeed.associated_tvss_feed) return;
    // get the cid that we actually need
    const selectedFeedCid = selectedFeed.associated_tvss_feed;
    setSelectedChannel(selectedFeedCid);
    setUserSelectedChannel(selectedFeedCid);

  }, [livestream_feeds]);

  const rootClassNames = `${styles.multi_livestream__root} ${styles[`multi_livestream__theme__${theme}`]}`;

  return (
    <PageTransition>
      <div className={rootClassNames}>
        {stationData && (
          <section className={styles.multi_livestream__app}>
          <MultiLivestreamPlayer
            host={host}
            isSVP={isSVP}
            stationId={stationId}
            selectedChannel={selectedChannel}
            userSelectedChannel={userSelectedChannel}
          />
          <MultiLivestreamStationInfo
            isIframe={isIframe}
            scheduleUrl={tvss_url}
            stationName={short_common_name}
            primaryTimezone={timezone}
            secondaryTimezone={secondary_timezone}
          />
          {dataFetchError ? (
            <MultiLivestreamError
              isIframe={isIframe}
              stationName={short_common_name}
              scheduleUrl={tvss_url}
            />
          ) : (
            <MultiLivestreamSchedule
              selectedChannel={userSelectedChannel || selectedChannel}
              setUserSelectedChannel={setUserSelectedChannel}
              setDataFetchError={setDataFetchError}
              stationId={stationId}
              primaryTimezone={timezone}
              theme={theme}
            />
          )}
        </section>
        )}
      </div>
    </PageTransition>
  );
}

export default MultiLivestream
